/* eslint-disable padding-line-between-statements -- spaces added for readability */
/* eslint-disable sonarjs/no-nested-conditional -- nesting improves readability */
/* eslint-disable unicorn/no-nested-ternary -- nesting improves readability */
// eslint-disable-next-line import/no-useless-path-segments -- irrelevant
import { logData } from "./index";

export type ManageTripsTelemetrySchemaVersion = "1.0";

export type ManageTripsTelemetryApp = "ManageTripsNext";

export type ManageTripsTelemetryDomain = "manage-trips";

export type ManageTripsTelemetryFeature =
  | "authenticated-trips"
  | "global"
  | "pnr-locator"
  | "qualifier";

export type ManageTripsTelemetryHttpError = Error & {
  status?: number;
  statusCode?: number;
  httpStatus?: number;
  errorCode?: string;
  code?: string;
};

export type ManageTripsTelemetryOperation =
  | "anonymous_locator_submit"
  | "generate_open_token"
  | "itinerary_route"
  | "javascript_error"
  | "pnr_health_check"
  | "pnr_lookup"
  | "qualifier_update"
  | "route_decision"
  | "sabre_dx_redirect"
  | "upcoming_trips_fetch"
  | "validate_open_token";

export type ManageTripsTelemetryFlow =
  | "anonymous"
  | "authenticated"
  | "global"
  | "qualifier";

export type ManageTripsTelemetryStatus =
  | "expired"
  | "failure"
  | "skipped"
  | "started"
  | "success";

export type ManageTripsTelemetryResultRoute =
  | "add-trip-email"
  | "itinerary"
  | "none"
  | "sabre-dx";

export type ManageTripsTelemetryLocatorType =
  | "record_locator"
  | "ticket_number"
  | "unknown";

export type ManageTripsTelemetryOpenTokenTtlBucket =
  | "expired"
  | "healthy"
  | "low"
  | "missing"
  | "unknown";

export type ManageTripsTelemetryTripCountBucket = "0" | "1" | "2-5" | "6+";

export type ManageTripsTelemetryEvent = {
  eventName: string;
  feature: ManageTripsTelemetryFeature;
  flow: ManageTripsTelemetryFlow;
  operation: ManageTripsTelemetryOperation;
  status: ManageTripsTelemetryStatus;

  durationMs?: number;
  emailExists?: boolean;
  errorCode?: string;
  errorType?: string;
  hasOpenToken?: boolean;
  httpStatus?: number;
  locatorType?: ManageTripsTelemetryLocatorType;
  openTokenTtlBucket?: ManageTripsTelemetryOpenTokenTtlBucket;
  phoneExists?: boolean;
  pnrState?: string;
  resultRoute?: ManageTripsTelemetryResultRoute;
  tripCountBucket?: ManageTripsTelemetryTripCountBucket;
};

export type ManageTripsTelemetryPayload = ManageTripsTelemetryEvent & {
  app: ManageTripsTelemetryApp;
  domain: ManageTripsTelemetryDomain;
  telemetrySchemaVersion: ManageTripsTelemetrySchemaVersion;
};

export type ErrorTelemetry = {
  errorCode?: string;
  errorType: string;
  httpStatus?: number;
};

const ACTION_TYPE = "manage_trips_telemetry";

/**
 * These field names are explicitly allowed even though some contain words
 * like "token", "locator", "email", or "phone".
 *
 * These are safe dashboard dimensions, not raw sensitive values.
 */
const ALLOWED_TELEMETRY_KEYS = new Set([
  "app",
  "domain",
  "durationMs",
  "emailExists",
  "errorCode",
  "errorType",
  "eventName",
  "feature",
  "flow",
  "hasOpenToken",
  "httpStatus",
  "locatorType",
  "openTokenTtlBucket",
  "operation",
  "phoneExists",
  "pnrState",
  "resultRoute",
  "status",
  "telemetrySchemaVersion",
  "tripCountBucket",
]);

/**
 * Raw PII / token / reservation field names that should never be accepted.
 *
 * Important:
 * Do not use broad patterns like /email/, /phone/, /token/, /locator/,
 * because safe fields such as emailExists, phoneExists, locatorType,
 * hasOpenToken, and openTokenTtlBucket are intentionally allowed.
 */
const FORBIDDEN_EXACT_KEYS = new Set([
  "accessToken",
  "access_token",
  "confirmationCode",
  "confirmation_code",
  "dateOfBirth",
  "dateofBirth",
  "dob",
  "email",
  "emailAddress",
  "firstName",
  "lastName",
  "loginID",
  "openToken",
  "phone",
  "phoneNumber",
  "pnr",
  "pnrRecordLocator",
  "recordLocator",
  "sabreOpenToken",
  "sabreToken",
  "ticketNumber",
  "trueBlueNumber",
  "tbNumber",
]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function assertNoForbiddenKeys(value: unknown, path = "event"): void {
  if (!isPlainObject(value)) {
    return;
  }

  for (const [key, nestedValue] of Object.entries(value)) {
    const fullPath = `${path}.${key}`;

    if (!ALLOWED_TELEMETRY_KEYS.has(key) && FORBIDDEN_EXACT_KEYS.has(key)) {
      throw new Error(
        `Manage Trips telemetry rejected forbidden field "${fullPath}". Do not log PII, tokens, PNRs, record locators, ticket numbers, emails, phone numbers, DOB, or TrueBlue values.`,
      );
    }

    assertNoForbiddenKeys(nestedValue, fullPath);
  }
}

function normalizeDurationMs(
  durationMs: number | undefined,
): number | undefined {
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs)) {
    return undefined;
  }

  return Math.max(0, Math.round(durationMs));
}

function sanitizeString(value: string | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return undefined;
  }

  return trimmed.slice(0, 120);
}

function sanitizeHttpStatus(
  httpStatus: number | undefined,
): number | undefined {
  if (typeof httpStatus !== "number" || !Number.isInteger(httpStatus)) {
    return undefined;
  }

  if (httpStatus < 100 || httpStatus > 599) {
    return undefined;
  }

  return httpStatus;
}

export function createManageTripsHttpError({
  errorCode,
  message,
  status,
}: {
  errorCode?: string;
  message: string;
  status?: number;
}): ManageTripsTelemetryHttpError {
  return Object.assign(new Error(message), {
    code: errorCode,
    errorCode,
    httpStatus: status,
    status,
    statusCode: status,
  });
}

export function getTelemetryStart(): number {
  return typeof performance !== "undefined" &&
    typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

export function getDurationMs(start: number): number {
  const now =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();

  return Math.max(0, Math.round(now - start));
}

export function getTripCountBucket(
  tripCount: number | undefined,
): ManageTripsTelemetryTripCountBucket | undefined {
  if (typeof tripCount !== "number" || !Number.isFinite(tripCount)) {
    return undefined;
  }

  if (tripCount <= 0) {
    return "0";
  }

  if (tripCount === 1) {
    return "1";
  }

  if (tripCount <= 5) {
    return "2-5";
  }

  return "6+";
}

export function getOpenTokenTtlBucket(
  ttlSeconds: number | undefined,
): ManageTripsTelemetryOpenTokenTtlBucket {
  if (typeof ttlSeconds !== "number" || !Number.isFinite(ttlSeconds)) {
    return "unknown";
  }

  if (ttlSeconds <= 0) {
    return "expired";
  }

  if (ttlSeconds < 300) {
    return "low";
  }

  return "healthy";
}

export function getErrorTelemetry(error: unknown): ErrorTelemetry {
  if (!isPlainObject(error)) {
    return {
      errorType: error instanceof Error ? error.name : "UnknownError",
    };
  }

  const maybeStatus =
    typeof error.status === "number"
      ? error.status
      : typeof error.statusCode === "number"
        ? error.statusCode
        : typeof error.httpStatus === "number"
          ? error.httpStatus
          : undefined;

  const maybeCode =
    typeof error.code === "string"
      ? error.code
      : typeof error.errorCode === "string"
        ? error.errorCode
        : undefined;

  const maybeName =
    typeof error.name === "string"
      ? error.name
      : typeof error.type === "string"
        ? error.type
        : undefined;

  return {
    errorCode: sanitizeString(maybeCode),
    errorType: sanitizeString(maybeName) ?? "UnknownError",
    httpStatus: sanitizeHttpStatus(maybeStatus),
  };
}

/**
 * Strict payload builder.
 *
 * This function is intentionally allowed to throw so tests can verify
 * PII/token guardrails.
 *
 * Important:
 * Validate the raw incoming event before constructing the sanitized payload.
 * Otherwise, accidental extra forbidden fields could be dropped before
 * validation and never detected.
 */
export function buildManageTripsTelemetryPayload(
  event: ManageTripsTelemetryEvent,
): ManageTripsTelemetryPayload {
  assertNoForbiddenKeys(event);

  /* eslint-disable sort-keys -- ordered for readability */
  const payload: ManageTripsTelemetryPayload = {
    app: "ManageTripsNext",
    domain: "manage-trips",
    telemetrySchemaVersion: "1.0",

    eventName: sanitizeString(event.eventName) ?? "unknown_event",
    feature: event.feature,
    flow: event.flow,
    operation: event.operation,
    status: event.status,

    durationMs: normalizeDurationMs(event.durationMs),
    emailExists: event.emailExists,
    errorCode: sanitizeString(event.errorCode),
    errorType: sanitizeString(event.errorType),
    hasOpenToken: event.hasOpenToken,
    httpStatus: sanitizeHttpStatus(event.httpStatus),
    locatorType: event.locatorType,
    openTokenTtlBucket: event.openTokenTtlBucket,
    phoneExists: event.phoneExists,
    pnrState: sanitizeString(event.pnrState),
    resultRoute: event.resultRoute,
    tripCountBucket: event.tripCountBucket,
  };
  /* eslint-enable sort-keys */

  assertNoForbiddenKeys(payload);

  return Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- irrelevant
    Object.entries(payload).filter(([, value]) => value != undefined),
  ) as ManageTripsTelemetryPayload;
}

/**
 * Production-safe telemetry boundary.
 *
 * Telemetry must never block or break the user journey. Payload construction
 * remains strict and testable, but runtime logging failures are swallowed here.
 */
export function logManageTripsEvent(event: ManageTripsTelemetryEvent): void {
  try {
    const payload = buildManageTripsTelemetryPayload(event);

    logData(payload, ACTION_TYPE).catch((error: unknown) => {
      console.warn("[manage-trips telemetry] logData failed", error);
    });
  } catch (error) {
    console.warn("[manage-trips telemetry] logManageTripsEvent failed", error);
  }
}

export async function withManageTripsTelemetry<T>(
  event: Omit<ManageTripsTelemetryEvent, "durationMs" | "status">,
  callback: () => Promise<T>,
): Promise<T> {
  const start = getTelemetryStart();

  logManageTripsEvent({
    ...event,
    status: "started",
  });

  try {
    const result = await callback();

    logManageTripsEvent({
      ...event,
      durationMs: getDurationMs(start),
      status: "success",
    });

    return result;
  } catch (error) {
    const errorTelemetry = getErrorTelemetry(error);

    logManageTripsEvent({
      ...event,
      ...errorTelemetry,
      durationMs: getDurationMs(start),
      status: "failure",
    });

    throw error;
  }
}
/* eslint-enable padding-line-between-statements */
/* eslint-enable sonarjs/no-nested-conditional */
/* eslint-enable unicorn/no-nested-ternary */
